import { forwardRef, HTMLAttributes, ReactNode, Ref, useRef } from 'react';

import { IComponent, IMessageBarMessage, MessageBarListType } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { singleOrArrayToArray } from '../../utils/utils';
import { MessageBar } from '../MessageBar/MessageBar';
import {
  messageBarContainerStylesNotification,
  messageBarContentStylesNotification,
  messageBarStylesNotification,
} from '../MessageBar/MessageBar.styles';
import { IMessageBarListStyles, messageBarListStylesDefault } from './MessageBarList.styles';

interface IMessageBarListClassNames {
  root: string;
  messageBar: string;
}

export interface IMessageBarListProps extends IComponent<IMessageBarListStyles>, HTMLAttributes<HTMLDivElement> {
  autoClose?: boolean;
  autoCloseDelay?: number;
  messageBarListType?: MessageBarListType;
  messages: IMessageBarMessage[];
  groupByMessage?: boolean;
  onRemoveMessage: (message: ReactNode, messageBarMessage: IMessageBarMessage) => void;
  onClickMessage?: (e: React.MouseEvent, message: IMessageBarMessage) => void;
}

const getGroupByMessage = (messages: IMessageBarMessage[]): IMessageBarMessage[] => {
  const messageMap = new Map<ReactNode, IMessageBarMessage>();
  messages.forEach((m) => {
    const message = messageMap.get(m.message);
    if (message) {
      m.count = (message.count || 1) + 1;
    }
    messageMap.set(m.message, m);
  });
  return Array.from(messageMap.values());
};

export const MessageBarList = forwardRef((props: IMessageBarListProps, ref: Ref<HTMLDivElement>) => {
  const refPercentMap = useRef<Map<string | undefined, number>>(new Map<string | undefined, number>());
  const {
    className,
    messages,
    onRemoveMessage,
    onClickMessage,
    styles,
    autoClose,
    autoCloseDelay = 5000,
    messageBarListType,
    groupByMessage,
    ...elementProps
  } = props;
  const classNames = useClassNames<IMessageBarListStyles, IMessageBarListClassNames>(
    'MessageBarList',
    messageBarListStylesDefault,
    styles,
    className
  );
  return messages.length ? (
    <div {...elementProps} ref={ref} className={classNames.root} data-test={props['data-test'] || 'message-bar-list'}>
      {(groupByMessage ? getGroupByMessage(messages) : messages).map((m, i) => {
        const {
          id,
          message,

          groupId,

          data,
          hideDismissButton,
          onDismiss,
          onClick,
          onMouseEnter,
          timeout,
          styles,
          messageBarContentProps,
          messageBarContainerStyles: conStyles,
          ...messageBarProps
        } = m;
        let messageBarStyles = singleOrArrayToArray(styles);
        let messageBarContainerStyles = singleOrArrayToArray(conStyles);
        let messageBarContentStyles = singleOrArrayToArray(messageBarContentProps?.styles);
        if (messageBarListType === MessageBarListType.notification) {
          messageBarStyles = [messageBarStylesNotification, ...messageBarStyles];
          messageBarContainerStyles = [messageBarContainerStylesNotification, ...messageBarContainerStyles];
          messageBarContentStyles = [messageBarContentStylesNotification, ...messageBarContentStyles];
        }
        return (
          <div key={id || i} className={classNames.messageBar}>
            <MessageBar
              {...messageBarProps}
              styles={messageBarStyles}
              messageBarContainerStyles={messageBarContainerStyles}
              messageBarContentProps={{
                ...messageBarContentProps,
                styles: messageBarContentStyles,
              }}
              timeout={autoClose ? timeout || autoCloseDelay : 0}
              timeoutPercentComplete={refPercentMap.current.get(id)}
              onDismiss={
                hideDismissButton
                  ? undefined
                  : () => {
                      refPercentMap.current.delete(id);
                      if (onDismiss) onDismiss();
                      // when removing this message, it is still rendering and try to update this component by the removed message
                      // that causes "Warning: Cannot update a component (`Unknown`) while rendering a different component (`MessageBar`)...". so added setTimeout to avoid it.
                      setTimeout(() => onRemoveMessage(message, m));
                    }
              }
              onTimeoutPercentComplete={(percent) => refPercentMap.current.set(id, percent)}
              onClick={
                onClick || onClickMessage
                  ? (e) => {
                      if (onClickMessage) {
                        onClickMessage(e, { ...m });
                      }
                      if (onClick) {
                        onClick(e, { ...m });
                      }
                    }
                  : undefined
              }
              onMouseEnter={onMouseEnter ? (e) => onMouseEnter(e, { ...m }) : undefined}
            >
              {message}
            </MessageBar>
          </div>
        );
      })}
    </div>
  ) : null;
});
